import styled, { css, keyframes } from 'styled-components'
import { tokens } from 'styling/tokens'
import { ISafetyAlarmProps, ISafetyToneProps } from 'types/safetyHub.interface'

const TONE_COLOR: { [k in ISafetyToneProps['$tone']]: string } = {
  primary: tokens.text.primary,
  success: tokens.status.success.fg,
  info: tokens.status.info.fg,
  warning: tokens.status.warning.fg,
  danger: tokens.status.danger.fg,
  muted: tokens.text.muted,
}

const alarmPulse = keyframes`
  0% {
    box-shadow: 0 0 0.5rem ${tokens.status.danger.fg}, 0 0 0 0 ${tokens.status.danger.bg};
    opacity: 1;
  }
  70% {
    box-shadow: 0 0 0.5rem ${tokens.status.danger.fg}, 0 0 0 0.5rem transparent;
    opacity: 0.6;
  }
  100% {
    box-shadow: 0 0 0.5rem ${tokens.status.danger.fg}, 0 0 0 0 transparent;
    opacity: 1;
  }
`

export const KpiStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin: 2rem 0;

  @media (max-width: 70rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

export const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  align-items: stretch;

  @media (max-width: 90rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 50rem) {
    grid-template-columns: minmax(0, 1fr);
  }
`

export const AlarmBanner = styled.div<ISafetyAlarmProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 0.75rem 1rem;
  border-radius: ${tokens.radius.control};
  border: 1px solid ${tokens.status.danger.fg};
  background: ${tokens.status.danger.bg};

  ${({ $fatal }) =>
    $fatal &&
    css`
      border-width: 2px;
      box-shadow: 0 0 1.5rem -0.5rem ${tokens.status.danger.fg};
    `}
`

export const AlarmContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
`

export const AlarmDot = styled.span`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 999px;
  flex-shrink: 0;
  background: ${tokens.status.danger.fg};
  animation: ${alarmPulse} 1.6s ${tokens.motion.ease} infinite;
`

export const AlarmText = styled.p<ISafetyAlarmProps>`
  font-size: ${tokens.font.body};
  line-height: 1.5;
  color: ${tokens.status.danger.fg};
  font-weight: ${({ $fatal }) => ($fatal ? 800 : 600)};
  ${({ $fatal }) => $fatal && 'letter-spacing: 0.02em;'}
`

export const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

export const BreakdownRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

export const RowLabel = styled.span<ISafetyToneProps>`
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${({ $tone }) => TONE_COLOR[$tone]};
  font-weight: ${({ $tone, $max }) =>
    $max ? 800 : $tone === 'muted' || $tone === 'primary' ? 400 : 600};
`

export const RowValue = styled.span<ISafetyToneProps>`
  font-size: ${({ $max }) => ($max ? '1rem' : '0.875rem')};
  line-height: 1.25rem;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) => TONE_COLOR[$tone]};
  font-weight: ${({ $tone, $max }) => ($max ? 800 : $tone === 'muted' ? 400 : 700)};
`
